import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import { App } from './app'
import { Scenario } from './scenario'
import { Tenant } from './tenant'
import { User } from './user'
import { Zone } from './zone'

/**
 * The AttainApps platform contains a variety of core concepts and most
 * functionality occurs in relation to some combination of those core concepts
 *
 * For example, data records exist within a {@link Zone} in a {@link Tenant}.
 * {@link User Users} often interact with features in a {@link Scenario} in an
 * {@link App} in a {@link Zone} in a {@link Tenant}
 *
 * In many cases, we need to identify the combination of instances of these
 * various core concepts when enacting such functionality. This type is a
 * convenient way to identify such instances
 *
 * @see {@link Context.ForTenant}, {@link Context.ForZone},
 * {@link Context.ForScenario}, ...
 */
export interface Context {
  readonly appId?: App.Id
  readonly zoneId?: Zone.Id
  readonly scenarioId?: Scenario.Id
  readonly tenantId?: Tenant.Id
  readonly userId?: User.Id
}
export namespace Context {
  export type Require<K extends keyof Context> = Required.Semi<Context, K>
  export type Subset<K extends keyof Context> = Pick<Context, K>
  export type RequireSubset<K extends keyof Context> = Required<Subset<K>>

  /**
   * Represents a type of item that a {@link Context} can represent
   */
  export type Type = TypeHelper
  /**
   * Allows us to distribute over keyof {@link Context}
   *
   * We don't want to {@link Type} to have a type parameter, so we make this a
   * separate helper type
   */
  type TypeHelper<K extends keyof Context = keyof Context> =
    K extends `${infer TType}Id` ? TType : never

  /**
   * Returns the {@link Context} for a given {@link Type}
   */
  export type Of<TType extends Type> = {
    app: Context.ForApp
    scenario: Context.ForScenario
    tenant: Context.ForTenant
    zone: Context.ForZone
    user: Context.ForUser
  }[TType]

  /**
   * In some cases, we accept multiple {@link Context.Type} and behave
   * differently based on that type. Consider the following example:
   * ```ts
   * function doSomethingInZoneSchema(context: ents.core.admin.Context.ForZone): Promise<void> {
   *   const schemaName = pg.Schema.Name.of(context)
   *   ...
   * }
   *
   * declare const currentContext: ents.core.admin.Context.ForScenario
   * // A scenario context is assignable to a zone context, so this should not
   * // be a problem. But oh no! The schema name used in this function is
   * // actually for the scenario, not the zone. This violates the Liskov Substitution Principle.
   * doSomethingInZoneSchema(currentContext)
   * ```
   *
   * After encountering the above bug a couple times, we created this type so
   * that now, you need to be explicit. For example:
   * ```ts
   * function doSomethingInZoneSchema(context: ents.core.admin.Context.ForZone): Promise<void> {
   *   const schemaName = pg.Schema.Name.of({ zone: context })
   *   ...
   * }
   * ```
   */
  export type OneOf<TType extends Type> = ExactlyOneOf<{
    [K in TType]: Of<K>
  }>

  /**
   * Returns a straightforward {@link Context} from a {@link Context.OneOf}
   *
   * @example
   * declare oneOfContext: Context.OneOf<'zone' | 'app' | 'scenario'>
   * const { zoneId, appId, scenarioId } = Context.from(oneOfContext)
   */
  export function from<TType extends Type>(context: OneOf<TType>): Of<TType> {
    const { app, scenario, tenant, user, zone } = context as OneOf<Type>
    // We prioritize user, and then most-specific to least-specific for the
    // other contexts
    return (user ??
      scenario ??
      app ??
      zone ??
      tenant) satisfies Context as Of<TType>
  }

  /**
   * Represents any {@link Context} that has all necessary IDs to fully
   * reference one of:
   * 1. {@link Tenant}
   * 1. {@link Zone}
   * 1. {@link App}
   * 1. {@link Scenario}
   * 1. {@link User} or a {@link User} in relation to one of the above (i.e.
   *    {@link Context.ForUser.InAny})
   */
  export type ForAny =
    | ForTenant
    | ForZone
    | ForApp
    | ForScenario
    | ForUser
    | ForUser.InAny
  export namespace ForAny {
    export const isForAny: TypeChecker<ForAny> = TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.union(
        ForTenant.isForTenant,
        ForZone.isForZone,
        ForApp.isForApp,
        ForScenario.isForScenario,
        ForUser.isForUser,
        ForUser.InAny.isInAny,
      ),
    ).usingAlias('Context.ForAny')
    export const assertIsForAny: TypeAsserter<ForAny> =
      TypeAsserter.Factory.from(isForAny)

    /**
     * Reconstructs the most-specific valid context from a flat set of IDs.
     * Hierarchical contexts require their parents: a scenario requires an app,
     * an app requires a zone, and a zone requires a tenant.
     */
    export function fromIds(context: Context): ForAny {
      const { appId, scenarioId, tenantId, userId, zoneId } = context

      if (scenarioId !== undefined) {
        if (appId === undefined) {
          throw new Error('A scenario context requires an app ID')
        }
        if (zoneId === undefined || tenantId === undefined) {
          throw new Error('An app context requires a zone and tenant ID')
        }

        const scenarioContext: ForScenario = {
          appId,
          scenarioId,
          tenantId,
          zoneId,
        }
        return userId === undefined
          ? scenarioContext
          : { ...scenarioContext, userId }
      }

      if (appId !== undefined) {
        if (zoneId === undefined || tenantId === undefined) {
          throw new Error('An app context requires a zone and tenant ID')
        }

        const appContext: ForApp = { appId, tenantId, zoneId }
        return userId === undefined ? appContext : { ...appContext, userId }
      }

      if (zoneId !== undefined) {
        if (tenantId === undefined) {
          throw new Error('A zone context requires a tenant ID')
        }

        const zoneContext: ForZone = { tenantId, zoneId }
        return userId === undefined ? zoneContext : { ...zoneContext, userId }
      }

      if (tenantId !== undefined) {
        const tenantContext: ForTenant = { tenantId }
        return userId === undefined
          ? tenantContext
          : { ...tenantContext, userId }
      }

      if (userId !== undefined) {
        return { userId }
      }

      throw new Error('A context requires at least one ID')
    }
  }

  /**
   * References a {@link Tenant}
   */
  export type ForTenant = RequireSubset<'tenantId'>
  export namespace ForTenant {
    export const isForTenant: TypeChecker<ForTenant> = TypeChecker.Factory.lazy(
      () =>
        TypeChecker.Factory.object<ForTenant>({
          tenantId: Tenant.Id.isId,
        }),
    ).usingAlias('Context.ForTenant')
    export const assertIsForTenant: TypeAsserter<ForTenant> =
      TypeAsserter.Factory.from(isForTenant)
  }

  /**
   * References a {@link Zone} in a {@link Tenant}
   */
  export type ForZone = ForTenant & RequireSubset<'zoneId'>
  export namespace ForZone {
    export const isForZone: TypeChecker<ForZone> = TypeChecker.Factory.lazy(
      () =>
        TypeChecker.Factory.intersection(
          ForTenant.isForTenant,
          TypeChecker.Factory.object<Difference<ForZone, ForTenant>>({
            zoneId: Zone.Id.isId,
          }),
        ),
    ).usingAlias('Context.ForZone')
    export const assertIsForZone: TypeAsserter<ForZone> =
      TypeAsserter.Factory.from(isForZone)
  }

  /**
   * References a {@link App} in a {@link Zone} in a {@link Tenant}
   */
  export type ForApp = ForZone & RequireSubset<'appId'>
  export namespace ForApp {
    export const isForApp: TypeChecker<ForApp> = TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.intersection(
        ForZone.isForZone,
        TypeChecker.Factory.object<Difference<ForApp, ForZone>>({
          appId: App.Id.isId,
        }),
      ),
    ).usingAlias('Context.ForApp')
    export const assertIsForApp: TypeAsserter<ForApp> =
      TypeAsserter.Factory.from(isForApp)
  }

  /**
   * References a {@link Scenario} in an {@link App} in a {@link Zone} in a
   * {@link Tenant}
   */
  export type ForScenario = ForApp & RequireSubset<'scenarioId'>
  export namespace ForScenario {
    export const isForScenario: TypeChecker<ForScenario> =
      TypeChecker.Factory.lazy(() =>
        TypeChecker.Factory.intersection(
          ForApp.isForApp,
          TypeChecker.Factory.object<Difference<ForScenario, ForApp>>({
            scenarioId: Scenario.Id.isId,
          }),
        ),
      ).usingAlias('Context.ForScenario')
    export const assertIsForScenario: TypeAsserter<ForScenario> =
      TypeAsserter.Factory.from(isForScenario)
  }

  /**
   * References a {@link User}
   *
   * @see {@link Context.ForUser.InTenant}, {@link Context.ForUser.InZone},
   * {@link Context.ForUser.InScenario}, ...
   */
  export type ForUser = RequireSubset<'userId'>
  export namespace ForUser {
    export const isForUser: TypeChecker<ForUser> = TypeChecker.Factory.lazy(
      () =>
        TypeChecker.Factory.object<ForUser>({
          userId: User.Id.isId,
        }),
    ).usingAlias('Context.ForUser')
    export const assertIsForUser: TypeAsserter<ForUser> =
      TypeAsserter.Factory.from(isForUser)

    /**
     * References one of:
     * 1. {@link User.InTenant}
     * 1. {@link User.InZone}
     * 1. {@link User.InApp}
     * 1. {@link User.InScenario}
     */
    export type InAny = InTenant | InZone | InApp | InScenario
    export namespace InAny {
      export const isInAny: TypeChecker<InAny> = TypeChecker.Factory.lazy(() =>
        TypeChecker.Factory.union(
          InTenant.isInTenant,
          InZone.isInZone,
          InApp.isInApp,
          InScenario.isInScenario,
        ),
      ).usingAlias('Context.ForUser.InAny')
    }

    /**
     * References a {@link User.InTenant}
     */
    export type InTenant = ForUser & ForTenant
    export namespace InTenant {
      export const isInTenant: TypeChecker<InTenant> = TypeChecker.Factory.lazy(
        () =>
          TypeChecker.Factory.intersection(
            ForUser.isForUser,
            ForTenant.isForTenant,
          ),
      ).usingAlias('Context.ForUser.InTenant')
      export const assertIsInTenant: TypeAsserter<InTenant> =
        TypeAsserter.Factory.from(isInTenant)
    }

    /**
     * References a {@link User.InZone}
     */
    export type InZone = ForUser & ForZone
    export namespace InZone {
      export const isInZone: TypeChecker<InZone> = TypeChecker.Factory.lazy(
        () =>
          TypeChecker.Factory.intersection(
            ForUser.isForUser,
            ForZone.isForZone,
          ),
      ).usingAlias('Context.ForUser.InZone')
      export const assertIsInZone: TypeAsserter<InZone> =
        TypeAsserter.Factory.from(isInZone)
    }

    /**
     * References a {@link User.InApp}
     */
    export type InApp = ForUser & ForApp
    export namespace InApp {
      export const isInApp: TypeChecker<InApp> = TypeChecker.Factory.lazy(() =>
        TypeChecker.Factory.intersection(ForUser.isForUser, ForApp.isForApp),
      ).usingAlias('Context.ForUser.InApp')
      export const assertIsInApp: TypeAsserter<InApp> =
        TypeAsserter.Factory.from(isInApp)
    }

    /**
     * References a {@link User.InScenario}
     */
    export type InScenario = ForUser & ForScenario
    export namespace InScenario {
      export const isInScenario: TypeChecker<InScenario> =
        TypeChecker.Factory.lazy(() =>
          TypeChecker.Factory.intersection(
            ForUser.isForUser,
            ForScenario.isForScenario,
          ),
        ).usingAlias('Context.ForUser.InScenario')
      export const assertIsInScenario: TypeAsserter<InScenario> =
        TypeAsserter.Factory.from(isInScenario)
    }
  }
}
